import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3BottomLeftIcon,
  HomeIcon,
  BanknotesIcon,
  XMarkIcon,
  Cog8ToothIcon,
  Squares2X2Icon,
  ListBulletIcon,
  Square2StackIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  ChartPieIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  ClockIcon,
  ArrowRightIcon,
  GiftIcon,
  PhotoIcon,
  // Hidden from the menu until launch (owner, 2026-10-16). Uncomment with the menu entries; the routes, the Settings → Release Notes entry and the probe stay.
  // SparklesIcon,
} from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { Link, Outlet, useLocation } from 'react-router-dom';
import LogoBlack from '../../assets/logo-black.png';
import LogoWhite from '../../assets/logo-white-brand.svg';
import tokenService from '../../utils/tokenService';
import { financeMenuFeatures } from '../../utils/connectModel';
import ConnectPendingBand from './connect-pending-band';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '../../store/slices/authSlice';
import userApi from '../../services/userApi';
import WalkingSpinner from '../spinner/WalkingSpinner';
import CompanyChanger from '../company-changer/company-changer';
import companyApi from '../../services/companyApi';
import settingsApi from '../../services/settingsApi';
import { fetchReferralMe } from '../../store/slices/referralSlice';
import useReferralNav from './use-referral-nav';
import {
  setCompany,
  setGlobalCompany,
  fetchCompanySettings,
  fetchConnectStatus,
} from '../../store/slices/companySlice';
import useGleapContext from '../../hooks/useGleapContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useReleaseNotesUnread from '../../views/release-notes/use-release-notes-unread';
import NewUpdatesDot from '../../views/release-notes/components/NewUpdatesDot';
import {
  activeSubItemHref,
  filterSidebar,
  isTopLevelActive,
  nextOpenMenu,
  parseStoredCompany,
  pickAssignedCompany,
} from './layout-utils';

export const adminPanelMenu = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
    roles: [100, 150, 200, 250],
    permissions: ['dashboard-get'],
  },
  {
    name: 'Companies',
    href: 'companies',
    icon: Square2StackIcon,
    roles: [100],
    permissions: ['company-list'],
  },
  {
    name: 'Orders',
    href: 'orders',
    icon: ListBulletIcon,
    roles: [100, 150, 200, 250],
    permissions: ['order-list'],
  },
  {
    name: 'Customers',
    href: 'customers',
    icon: UserGroupIcon,
    roles: [100],
    permissions: ['customer-list'],
  },
  {
    name: 'Kiosks',
    href: 'kiosks',
    icon: ComputerDesktopIcon,
    roles: [100],
    permissions: ['terminal-list'],
  },
  {
    name: 'Devices',
    href: 'devices',
    icon: DeviceTabletIcon,
    roles: [100],
    permissions: [],
  },
  {
    name: 'Finance',
    href: 'finance',
    icon: BanknotesIcon,
    roles: [100, 150, 200],
    permissions: ['finance-list'],
  },
  {
    name: 'Reports',
    href: 'reports',
    icon: ChartPieIcon,
    roles: [100],
    permissions: ['dashboard-get'],
  },
  {
    name: 'Image Library',
    href: 'image-library',
    icon: PhotoIcon,
    roles: [100],
    permissions: ['imagelibrary-admin'],
  },
  // Hidden from the menu until launch (owner, 2026-10-16). Uncomment to show it again; the routes, the Settings → Release Notes entry and the probe stay.
  // {
  //   // Dashboard changelog (spec 2026-10-16). Every role reads it; the
  //   // permission is one every role already holds, so the roles list is the
  //   // real gate.
  //   name: "What's new",
  //   href: 'release-notes',
  //   icon: SparklesIcon,
  //   roles: [100, 150, 200, 250, 300, 550],
  //   permissions: ['user-get'],
  //   // Hidden only while the API answers 404 to ReleaseNote/List (the fe
  //   // deployed ahead of the API); any other failure keeps the entry.
  //   feature: 'releaseNotes',
  // },
  {
    name: 'Settings',
    href: 'settings',
    icon: Cog8ToothIcon,
    children: [
      {
        name: 'Profile',
        href: 'settings/profile',
        roles: [100, 150, 200, 250, 300, 550],
        permissions: ['user-get'],
      },
      {
        name: 'Permissions',
        href: 'settings/permissions',
        roles: [100],
        permissions: ['permission-list'],
      },
      {
        name: 'Users',
        href: 'settings/users',
        roles: [100, 150, 200],
        permissions: ['user-list'],
      },
      {
        name: 'Subscriptions',
        href: 'settings/subscriptions',
        roles: [100],
        permissions: ['subscription-list'],
      },
      {
        name: 'Tags',
        href: 'settings/tags',
        roles: [100],
        permissions: ['company-delete'],
      },
      {
        name: 'Templates',
        href: 'settings/templates',
        roles: [100],
        permissions: ['setting-list'],
      },
      {
        // Release-notes admin surface (spec 2026-10-16 §2). Role 100 by
        // role; anyone else only with the releasenote-admin permission.
        name: 'Release Notes',
        href: 'settings/release-notes',
        roles: [100],
        permissions: ['releasenote-admin'],
        feature: 'releaseNotes',
      },
      {
        name: 'Finance Types',
        href: 'settings/finance-types',
        roles: [100],
        permissions: ['financetype-list'],
      },
      {
        name: 'Referral',
        href: 'settings/referral',
        roles: [100],
        permissions: ['referral-admin'],
      },
      {
        name: 'Preferences',
        href: 'settings/preferences',
        roles: [100, 150, 200, 250, 300],
        permissions: ['bankaccount-list'],
      },
      {
        name: 'Blocked Numbers',
        href: 'settings/blocked-numbers',
        roles: [100, 150, 200],
        permissions: ['bankaccount-list'],
      },
      {
        name: 'Terms & Conditions',
        href: 'settings/terms-and-conditions',
        roles: [100],
        permissions: ['term-upsert'],
      },
      {
        name: 'Ratings',
        href: 'settings/ratings',
        roles: [100],
        permissions: ['setting-list'],
      },
      {
        name: 'Login Logs',
        href: 'settings/login-logs',
        roles: [100],
        permissions: ['setting-list'],
      },
      {
        name: 'Kiosk (Esper, legacy)',
        href: 'https://thisway.esper.cloud/dashboard',
        roles: [100],
        permissions: ['term-upsert'],
      },
    ],
  },
];

export const dashboardMenu = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
    roles: [100, 150, 200, 250, 550],
    permissions: ['dashboard-get'],
  },
  {
    name: 'Orders',
    href: 'orders',
    icon: ListBulletIcon,
    roles: [100, 150, 200, 250, 550],
    permissions: ['order-list'],
  },
  // Customers restoran tarafında geçici kapatıldı — backend /Customer/Counts
  // şirket kapsamı tutarsızlığı (sayaç ≠ liste totalRecords) çözülünce geri açılacak.
  // {
  //   name: 'Customers',
  //   href: 'customers',
  //   icon: UserGroupIcon,
  //   roles: [150, 200, 250, 550],
  //   permissions: ['customer-list'],
  // },
  {
    name: 'Menu',
    href: 'menus',
    icon: Squares2X2Icon,
    children: [
      {
        name: 'Menu Editor',
        href: 'menus',
        roles: [150, 200, 250, 300],
        permissions: ['menu-list'],
      },
      {
        name: 'Import Menu',
        href: 'menus/import',
        roles: [150, 200, 250, 300],
        permissions: ['menu-list'],
      },
      {
        name: 'Inventory',
        href: 'inventory',
        roles: [150, 200, 250, 300],
        permissions: ['inventory-list'],
      },
    ],
  },
  {
    name: 'Stores',
    href: 'stores',
    icon: BuildingStorefrontIcon,
    roles: [150, 200],
    permissions: ['store-delete'],
  },
  {
    name: 'Finance',
    href: 'finance',
    icon: BanknotesIcon,
    children: [
      {
        // Hidden while the company is onboarding (owner, 2026-09-25): its
        // Finance is Business Details only until Activate.
        name: 'Overview',
        href: 'finance',
        roles: [100, 150, 200, 550],
        permissions: ['finance-list'],
        feature: 'financeOverview',
      },
      // Every entry below that mounts a Stripe embedded component inherits the
      // gate its Account Session needs (ConnectManagePermission + membership),
      // which is the gate Settings -> Payments carried before it moved here.
      // The ones that read our own records keep Finance's own gate, so the
      // accountant (550) can read the money without touching the bank.
      {
        name: 'Payouts',
        href: 'finance/payouts',
        roles: [150, 200],
        permissions: ['subscription-get'],
        feature: 'connectDirect',
      },
      {
        name: 'Transactions',
        href: 'finance/order-reports',
        roles: [150, 200, 550],
        permissions: ['finance-list'],
        feature: 'connectDirect',
      },
      {
        name: 'Disputes',
        href: 'finance/disputes',
        roles: [150, 200],
        permissions: ['subscription-get'],
        feature: 'connectDirect',
      },
      {
        // Direct charges only since 2026-09-25: an account on its way to
        // Connect shows Business Details alone (owner).
        name: 'Tax Documents',
        href: 'finance/tax-documents',
        roles: [150, 200],
        permissions: ['subscription-get'],
        feature: 'connectDirect',
      },
      {
        // Business Details. `connect` is "has a Stripe account, or born
        // Pending" (connectModel.js), so a Pending company whose account could
        // not be opened at signup still reaches the form that opens it.
        name: 'Business Details',
        href: 'finance/business-details',
        roles: [150, 200],
        permissions: ['subscription-get'],
        feature: 'connect',
      },
      {
        // Legacy companies that have not started Connect only. On Connect
        // the bank is part of Payouts; while onboarding, Finance is Business
        // Details alone (owner, 2026-09-25). The route itself still exists and
        // redirects (bank-account.js); what is gated here is the door.
        name: 'Bank Account',
        href: 'finance/bank-account',
        roles: [150, 200],
        permissions: ['subscription-get'],
        feature: 'legacyBank',
      },
    ],
  },
  {
    name: 'Reports',
    href: 'reports',
    icon: ChartPieIcon,
    roles: [150, 200, 550],
    permissions: ['dashboard-get'],
  },
  {
    name: 'Campaigns',
    href: 'campaigns',
    icon: CurrencyDollarIcon,
    roles: [150, 200],
    permissions: ['discount-list'],
  },
  {
    name: 'Refer & Earn',
    href: 'referral',
    icon: GiftIcon,
    roles: [150, 200],
    feature: 'referral',
  },
  // Hidden from the menu until launch (owner, 2026-10-16). Uncomment to show it again; the routes, the Settings → Release Notes entry and the probe stay.
  // {
  //   // Dashboard changelog (spec 2026-10-16). Every role reads it; the
  //   // permission is one every role already holds, so the roles list is the
  //   // real gate.
  //   name: "What's new",
  //   href: 'release-notes',
  //   icon: SparklesIcon,
  //   roles: [100, 150, 200, 250, 300, 550],
  //   permissions: ['user-get'],
  //   // Hidden only while the API answers 404 to ReleaseNote/List (the fe
  //   // deployed ahead of the API); any other failure keeps the entry.
  //   feature: 'releaseNotes',
  // },
  {
    name: 'Settings',
    href: 'settings',
    icon: Cog8ToothIcon,
    children: [
      {
        name: 'Profile',
        href: 'settings/profile',
        roles: [100, 150, 200, 250, 300, 550],
        permissions: ['user-get'],
      },
      {
        name: 'Company',
        href: `companies`,
        roles: [150],
        permissions: ['company-delete'],
      },
      {
        name: 'Users',
        href: 'settings/users',
        roles: [100, 150, 200],
        permissions: ['user-list'],
      },
      {
        name: 'Integrations',
        href: 'settings/integrations',
        roles: [100, 150, 200],
        permissions: ['user-list'],
      },
      {
        name: 'Subscription',
        href: 'settings/subscription',
        roles: [150, 200],
        permissions: ['subscription-get'],
      },
      {
        name: 'Preferences',
        href: 'settings/preferences',
        roles: [100, 150, 200, 250, 300],
        permissions: ['menu-list'],
      },
      {
        name: 'Printer Profiles',
        href: 'settings/printer-profiles',
        roles: [100, 150, 200],
        permissions: ['menu-list'],
      },
      {
        name: 'Legal',
        href: 'settings/legal',
        roles: [150, 200],
        permissions: ['user-get'],
      },
      {
        // Release-notes admin surface (spec 2026-10-16 §2). Role 100 by
        // role; anyone else only with the releasenote-admin permission.
        name: 'Release Notes',
        href: 'settings/release-notes',
        roles: [100],
        permissions: ['releasenote-admin'],
        feature: 'releaseNotes',
      },
    ],
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// The period picker on Overview/Order Reports/Refunds/Disputes/Payouts keeps
// its choice in the URL (?period, ?from, ?to — see
// src/views/finance/pages/use-finance-period.js) so it survives a refresh,
// but a plain `<Link to={subItem.href}>` drops those params on a sidebar
// move between two Finance sub-pages, silently resetting the period a
// restaurant just picked. Scoped to the Finance parent alone — every other
// sidebar section keeps its bare `subItem.href`, unaffected.
const FINANCE_PERIOD_PARAMS = ['period', 'from', 'to'];
export const financeSubItemHref = (item, subItem, search) => {
  if (item.name !== 'Finance') return subItem.href;
  const current = new URLSearchParams(search);
  const carried = new URLSearchParams();
  FINANCE_PERIOD_PARAMS.forEach((key) => {
    const value = current.get(key);
    if (value) carried.set(key, value);
  });
  const qs = carried.toString();
  return qs ? `${subItem.href}?${qs}` : subItem.href;
};

// Exactly the roles that can see any Connect-gated Finance entry: 150 and 200
// manage the account, 550 (Accountant) reads the money. Exported and kept as a
// pure check so the guard below can be pinned by a test without mounting the
// whole shell — see connect-nav.test.js.
//
// ConnectController's membership gate landed on 2026-09-13
// (CanManageConnectAsync: ConnectManagePermission + Admin OR CompanyUser), so
// these roles now get a real answer instead of the 422 an earlier version of
// this comment described. What they still need is the permission itself on
// their role; without it the status reads null and the whole section stays
// fail-closed on legacy.
export const CONNECT_STATUS_ROLES = [150, 200, 550];
export const canFetchConnectStatus = (userRoles) =>
  !!userRoles?.some((role) => CONNECT_STATUS_ROLES.includes(role));

const Dashboard = () => {
  const dispatch = useDispatch();
  let location = useLocation();
  const isDark = useSelector((state) => state.commonReducer.isDark);
  const userRoles = useSelector((state) => state.authReducer.roles);
  const userPermissions = useSelector(
    (state) => state.authReducer.rolepermissions,
  );
  const selectedCompany = useSelector((state) => state.companyReducer.selected);
  const companySettings = useSelector((state) => state.companyReducer.settings);
  // Enabled AND a reward above zero, for the company selected now.
  const referralEnabled = useReferralNav(userRoles?.includes(100));
  const connectStatus = useSelector((state) => state.companyReducer.connectStatus);
  useGleapContext();
  const titleMenu = useMemo(() => [...adminPanelMenu, ...dashboardMenu], []);
  useDocumentTitle(titleMenu);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebar, setSidebar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(
    location.pathname.split('dashboard/').length > 1
      ? location.pathname.split('dashboard/')[1]
      : '/dashboard',
  );
  // The "New" dot on What's new and the API-availability flag (spec §6).
  const { unread: showReleaseNotesDot, available: releaseNotesAvailable } =
    useReleaseNotesUnread(tokenService.getLocalAccessToken());

  // A parent item highlights only when it is the active route AND no child of
  // it owns that route. Some parents share an href with a child (e.g. "Menu"
  // and "Menu Editor" both → 'menus'); without this guard both would light up.
  const isParentActive = (item) =>
    current === item.href && !item.children?.some((c) => c.href === current);

  const [, setSettings] = useState([]);
  const getDetail = async () => {
    setLoading(true);
    try {
      const res = await userApi.getUserDetail();
      dispatch(loginSuccess(res.data.data));
    } catch (error) {
      console.log(error.response.data);
    } finally {
    }
  };

  const getCompanyList = async () => {
    const isAdmin = userRoles?.includes(100);
    try {
      const list = await companyApi.list();
      const companies = list.data?.data || [];
      if (companies.length > 0 && !isAdmin) {
        dispatch(setCompany(companies));
        const company = pickAssignedCompany(
          companies,
          parseStoredCompany(tokenService.getComp()),
        );
        if (company?.id) {
          dispatch(setGlobalCompany(company));
          dispatch(fetchCompanySettings(company.id));
          try {
            await dispatch(fetchReferralMe({ companyId: company.id, force: true })).unwrap();
          } catch {
            // referral verisi alınamadı -> menüde gösterilmez
          }
        }
      } else {
        dispatch(setCompany([]));
      }
    } catch (error) {
      console.error('Failed to load assigned companies', error);
      if (!isAdmin) dispatch(setCompany([]));
    } finally {
      setLoading(false);
    }
  };

  const getSettingsList = async () => {
    try {
      const list = await settingsApi.list();
      if (list.data.data.length > 0 && !userRoles.includes(100)) {
        setSettings(list.data.data);
      }
    } catch (error) {
    } finally {
    }
  };

  const calculateFreeTrialDate = (trialEnd) => {
    const today = new Date();
    const endDate = new Date(trialEnd);
    const diffTime = endDate - today;
    if (diffTime > 0) {
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return {
        expired: false,
        label: `${diffDays} ${diffDays > 1 ? 'days' : 'day'} left`,
        urgent: diffDays <= 3,
      };
    }
    return { expired: true, label: 'Trial expired', urgent: true };
  };

  // Feature-gated entries ('referral' from /Referral/Me, the Finance flags
  // from the company's Connect status via connectModel.js) resolve here; the
  // filter itself lives in layout-utils so it can be tested without mounting
  // the whole shell.
  const sidebarByRolePermissions = (
    permissions,
    userRoles,
    referralEnabled,
    financeFeatures,
  ) => {
    const sidebarMenu = userRoles?.some((role) => role === 100)
      ? adminPanelMenu
      : dashboardMenu;

    setSidebar(
      filterSidebar(sidebarMenu, {
        permissions,
        roles: userRoles,
        features: {
          referral: referralEnabled,
          ...financeFeatures,
        },
      }),
    );
  };

  useEffect(() => {
    getDetail();
    getSettingsList();
    getCompanyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenService.getLocalAccessToken(), userPermissions, userRoles]);

  // One read per company for the Finance Connect sub-menu — keyed on the
  // selected company rather than on mount, so a switch that does not reload
  // (or a company created in-session) re-reads instead of leaving the previous
  // company's answer in place. Failure is swallowed by the thunk (fail-closed:
  // no status -> no entries).
  //
  // Asked for exactly the roles that can see a Connect-gated Finance entry —
  // 150, 200 and 550 (CONNECT_STATUS_ROLES above), not merely "not the
  // platform admin": 250 and 300 are real roles too (see Preferences'
  // [100, 150, 200, 250, 300] a few lines up) that also cannot see any of
  // these entries, and asking on their behalf would buy nothing but a
  // tracked 4xx per dashboard mount.
  useEffect(() => {
    if (selectedCompany?.id && canFetchConnectStatus(userRoles)) {
      dispatch(fetchConnectStatus(selectedCompany.id));
    }
  }, [dispatch, selectedCompany?.id, userRoles]);

  // Sidebar; rol/izinleri ve referral özellik bayrağını yansıtır.
  // `referralEnabled` çözülünce "Refer & Earn" tam sayfa reload olmadan
  // görünür/gizlenir. The Connect status works the same way, but only once it
  // is next read — a Finance visit (finance-section.js's own mount effect and
  // refreshStatus both write this same store slice) or a reload — not the
  // instant an admin flips the payment model elsewhere; this effect itself
  // never re-reads on its own.
  //
  // Keyed on the four Finance flags, not on the status object: the status is
  // re-read (a new object) far more often than any flag changes, and every
  // rebuild is a chance to disturb the dropdown the user has open.
  const { connect, connectDirect, financeOverview, legacyBank } =
    financeMenuFeatures(connectStatus);
  useEffect(() => {
    sidebarByRolePermissions(userPermissions, userRoles, referralEnabled, {
      connect,
      connectDirect,
      financeOverview,
      legacyBank,
      releaseNotes: releaseNotesAvailable,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPermissions, userRoles, referralEnabled, connect, connectDirect, financeOverview, legacyBank, releaseNotesAvailable]);

  // Open the parent dropdown that contains the active route (e.g. "Menu" on
  // /dashboard/inventory) once the filtered sidebar is built — on a new page
  // only. A rebuild on the same page keeps the dropdown the user opened
  // (nextOpenMenu).
  const lastPathname = useRef(null);
  useEffect(() => {
    if (sidebar.length === 0) return;
    const routeChanged = lastPathname.current !== location.pathname;
    lastPathname.current = location.pathname;
    setOpenMenu((open) =>
      nextOpenMenu({ open, sidebar, pathname: location.pathname, routeChanged }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar, location.pathname]);

  return loading ? (
    <WalkingSpinner fullScreen size={88} />
  ) : (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white dark:bg-slate-800 pt-5 pb-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white dark:focus:ring-black"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white dark:text-sky-400"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex flex-shrink-0 items-center px-4">
                  <img
                    className="h-8 w-auto"
                    src={isDark ? LogoWhite : LogoBlack}
                    alt="Your Company"
                  />
                </div>
                <div className="mt-5 h-0 flex-1 overflow-y-auto">
                  <nav className="space-y-1 px-2">
                    {sidebar.map((item, itemIdx) =>
                      !item.children ? (
                        <Link
                          key={itemIdx}
                          to={item.href}
                          onClick={() => {
                            setCurrent(item.href);
                            setSidebarOpen(false);
                          }}
                          className={classNames(
                            isTopLevelActive(current, item.href)
                              ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                            'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
                          )}
                        >
                          <item.icon
                            className={classNames(
                              isTopLevelActive(current, item.href)
                                ? 'text-gray-500 dark:text-sky-400'
                                : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-700 dark:group-hover:text-slate-200',
                              'mr-3 flex-shrink-0 h-6 w-6',
                            )}
                            aria-hidden="true"
                          />
                          <span className="flex-1">{item.name}</span>
                          {item.href === 'release-notes' && showReleaseNotesDot ? <NewUpdatesDot /> : null}
                        </Link>
                      ) : (
                        <div as="div" key={item.name} className="space-y-1">
                          <>
                            <Link
                              onClick={() => {
                                setCurrent(item.href);
                                setOpenMenu((prev) => (prev === item.name ? null : item.name));
                              }}
                              key={itemIdx}
                              className={classNames(
                                isParentActive(item)
                                  ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                                'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none',
                              )}
                            >
                              <item.icon
                                className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                aria-hidden="true"
                              />
                              <span className="flex-1">{item.name}</span>
                              <svg
                                className={classNames(
                                  openMenu === item.name
                                    ? 'text-gray-400 rotate-90'
                                    : 'text-gray-300',
                                  'ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400',
                                )}
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                              >
                                <path
                                  d="M6 6L14 10L6 14V6Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </Link>
                            {openMenu === item.name && (
                              <div className="space-y-1">
                                {item.children.map((subItem, subItemIdx) => (
                                  <Link
                                    key={subItemIdx}
                                    to={financeSubItemHref(item, subItem, location.search)}
                                    onClick={() => {
                                      setCurrent(subItem.href);
                                      setSidebarOpen(false);
                                    }}
                                    className={classNames(
                                      subItem.href === activeSubItemHref(item, current)
                                        ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                                      'group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                    )}
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        </div>
                      ),
                    )}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col z-10">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 dark:border-slate-700  bg-white dark:bg-slate-900 pt-5">
          <div className="flex flex-shrink-0 items-center px-4">
            <img
              className="h-8 w-auto"
              src={isDark ? LogoWhite : LogoBlack}
              alt="Your Company"
            />
          </div>
          <div className="mt-5 flex flex-grow flex-col">
            <nav className="flex-1 space-y-1 px-2 pb-4">
              {sidebar.map((item, itemIdx) =>
                !item.children ? (
                  <Link
                    key={itemIdx}
                    to={item.href}
                    onClick={() => setCurrent(item.href)}
                    className={classNames(
                      isTopLevelActive(current, item.href)
                        ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                      'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
                    )}
                  >
                    <item.icon
                      className={classNames(
                        isTopLevelActive(current, item.href)
                          ? 'text-gray-500 dark:text-sky-400'
                          : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-700 dark:group-hover:text-slate-200',
                        'mr-3 flex-shrink-0 h-6 w-6',
                      )}
                      aria-hidden="true"
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.href === 'release-notes' && showReleaseNotesDot ? <NewUpdatesDot /> : null}
                  </Link>
                ) : (
                  <div as="div" key={item.name} className="space-y-1">
                    <>
                      <Link
                        onClick={() => {
                          setCurrent(item.href);
                          setOpenMenu((prev) => (prev === item.name ? null : item.name));
                        }}
                        key={itemIdx}
                        className={classNames(
                          isParentActive(item)
                            ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                          'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none',
                        )}
                      >
                        <item.icon
                          className={classNames(
                            isParentActive(item)
                              ? 'text-gray-500 dark:text-sky-400'
                              : 'text-gray-400 group-hover:text-gray-500 dark:text-slate-700 dark:group-hover:text-slate-200',
                            'mr-3 flex-shrink-0 h-6 w-6',
                          )}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{item.name}</span>
                        <svg
                          className={classNames(
                            openMenu === item.name
                              ? 'text-gray-400 dark:text-sky-400 rotate-90'
                              : 'text-gray-300 dark:text-slate-600',
                            'ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400',
                          )}
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                        </svg>
                      </Link>
                      {openMenu === item.name && (
                        <div className="space-y-1">
                          {item.children.map((subItem, subItemIdx) => (
                            <Link
                              key={subItemIdx}
                              to={financeSubItemHref(item, subItem, location.search)}
                              onClick={() => setCurrent(subItem.href)}
                              className={classNames(
                                subItem.href === activeSubItemHref(item, current)
                                  ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-sky-400'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700',
                                'group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                              )}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  </div>
                ),
              )}
            </nav>
          </div>
        </div>
      </div>
      <div className="flex flex-1 h-screen flex-col md:pl-64 ">
        <div className="sticky top-0 z-10 flex h-[8vh] flex-shrink-0 dark:border-b dark:border-slate-800 bg-white dark:bg-slate-900 shadow">
          <button
            type="button"
            className="border-r border-gray-200 dark:border-slate-700  px-4 text-gray-500  focus:outline-none focus:ring-0 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex flex-1 justify-between px-4 ">
            <div className="flex flex-1 dark:bg-slate-900"></div>
            <div className="ml-4 flex items-center space-x-4 md:ml-2">
              {userRoles.includes(150) &&
                !selectedCompany.subscription &&
                selectedCompany.trialend &&
                (() => {
                  const trial = calculateFreeTrialDate(
                    selectedCompany.trialend,
                  );
                  const urgent = trial.urgent || trial.expired;
                  return (
                    <Link
                      to="settings/subscription"
                      className={
                        'group inline-flex items-center gap-2 rounded-full px-3 py-1.5 ring-1 transition active:scale-[0.98] ' +
                        (urgent
                          ? 'bg-rose-50 ring-rose-200 hover:bg-rose-100 dark:bg-rose-500/10 dark:ring-rose-500/30 dark:hover:bg-rose-500/15'
                          : 'bg-blue-50 ring-blue-200 hover:bg-blue-100 dark:bg-sky-500/10 dark:ring-sky-500/30 dark:hover:bg-sky-500/15')
                      }
                    >
                      <span
                        className={
                          'flex h-6 w-6 items-center justify-center rounded-full ' +
                          (urgent
                            ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300'
                            : 'bg-blue-100 text-blue-600 dark:bg-sky-500/20 dark:text-sky-300')
                        }
                      >
                        <ClockIcon className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex flex-col items-start leading-tight">
                        <span
                          className={
                            'text-[0.65rem] font-semibold uppercase tracking-wider ' +
                            (urgent
                              ? 'text-rose-500 dark:text-rose-300'
                              : 'text-blue-500 dark:text-sky-300')
                          }
                        >
                          {trial.expired ? 'Trial' : 'Free trial'}
                        </span>
                        <span
                          className={
                            'text-xs font-bold ' +
                            (urgent
                              ? 'text-rose-700 dark:text-rose-100'
                              : 'text-blue-700 dark:text-sky-100')
                          }
                        >
                          {trial.label}
                        </span>
                      </span>
                      <span
                        className={
                          'flex items-center gap-0.5 text-[0.65rem] font-bold uppercase tracking-wider ' +
                          (urgent
                            ? 'text-rose-600 dark:text-rose-300'
                            : 'text-blue-600 dark:text-sky-300')
                        }
                      >
                        Upgrade
                        <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })()}
              <CompanyChanger />
            </div>
          </div>
        </div>

        {/* One sticky slot for the section-wide bands, so the payout-paused
            band and the Pending band (spec 2026-09-25 §5.6) stack rather
            than overlap. */}
        {!userRoles.includes(100) && (
          <div className="sticky top-[8vh] z-[1]">
            {companySettings?.payout_paused?.value === 'true' && (
              <div className="border-b border-rose-200 bg-rose-50 px-4 py-2.5 dark:border-rose-500/30 dark:bg-rose-500/10">
                <div className="flex items-center justify-center gap-2.5">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300">
                    <XCircleIcon className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-medium text-rose-900 dark:text-rose-100">
                    Your payouts have been paused. Please contact your account
                    manager for more information.
                  </p>
                </div>
              </div>
            )}
            <ConnectPendingBand status={connectStatus} roles={userRoles} />
          </div>
        )}
        <main className="flex-1 dark:text-slate-200 bg-white dark:bg-slate-800">
          <div className="">
            <div className="mx-auto w-full ">
              {!userRoles?.includes(100) && !selectedCompany?.id ? (
                <div className="flex min-h-[50vh] flex-col items-center justify-center px-6 text-center">
                  <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    No company assigned
                  </p>
                  <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                    Your account is not assigned to a company. Contact your
                    administrator.
                  </p>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
